import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  listChatMedia,
  getWhatsAppErrorMessage,
  unwrapWhatsAppList,
} from "../../../services/whatsappApi";
import { formatRelativeTime, resolveWhatsAppMediaUrl } from "../../../utils/whatsappFormat";

const MEDIA_PAGE_SIZE = 20;

const TABS = [
  {
    key: "image",
    label: "Images",
    countKey: "image",
    icon: "solar:gallery-linear",
    emptyIcon: "solar:gallery-linear",
    emptyText: "No images",
  },
  {
    key: "video",
    label: "Videos",
    countKey: "video",
    icon: "solar:play-circle-linear",
    emptyIcon: "solar:play-circle-linear",
    emptyText: "No videos",
  },
  {
    key: "audio",
    label: "Voice",
    countKey: "audio",
    icon: "solar:microphone-linear",
    emptyIcon: "solar:microphone-linear",
    emptyText: "No voice messages",
  },
  {
    key: "document",
    label: "Documents",
    countKey: "document",
    icon: "solar:document-linear",
    emptyIcon: "solar:document-linear",
    emptyText: "No documents",
  },
  {
    key: "location",
    label: "Location",
    countKey: "location",
    icon: "solar:map-point-linear",
    emptyIcon: "solar:map-point-linear",
    emptyText: "No locations",
  },
  {
    key: "contact",
    label: "Contacts",
    countKey: "contact",
    icon: "solar:user-id-linear",
    emptyIcon: "solar:user-id-linear",
    emptyText: "No contacts",
  },
];

const TYPE_ICONS = {
  image: "solar:gallery-linear",
  video: "solar:play-circle-linear",
  audio: "solar:microphone-linear",
  document: "solar:document-linear",
  location: "solar:map-point-linear",
  contact: "solar:user-id-linear",
};

function MediaThumb({ item, onImagePreview }) {
  const type = String(item.type || "").toLowerCase();
  const url = resolveWhatsAppMediaUrl(item.url);

  if (url && type === "image") {
    return (
      <button
        type="button"
        className="wa-media-thumb wa-media-thumb--clickable"
        onClick={() =>
          onImagePreview?.(url, item.caption || item.filename || "Image")
        }
        aria-label="Open image preview"
      >
        <img src={url} alt={item.caption || "image"} loading="lazy" />
      </button>
    );
  }

  if (url && type === "video") {
    return (
      <div className="wa-media-thumb">
        <video src={url} preload="metadata" muted />
      </div>
    );
  }

  return (
    <div className="wa-media-thumb wa-media-thumb--icon">
      <Icon icon={TYPE_ICONS[type] || "solar:file-linear"} width={28} />
    </div>
  );
}

export default function MediaAssetsPanel({
  chatId,
  mediaCounts: initialCounts,
  onImagePreview,
}) {
  const [activeType, setActiveType] = useState("image");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(initialCounts || {});
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (initialCounts) setCounts(initialCounts);
  }, [initialCounts]);

  const fetchMedia = useCallback(
    async ({ type, append = false, nextOffset = 0 } = {}) => {
      if (!chatId) return;
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const res = await listChatMedia(chatId, {
          type: type || "image",
          limit: MEDIA_PAGE_SIZE,
          offset: nextOffset,
        });
        const rows = unwrapWhatsAppList(res);
        setItems((prev) => (append ? [...prev, ...rows] : rows));
        setTotal(Number(res?.total) || rows.length);
        setOffset(nextOffset);
        if (res?.counts) setCounts(res.counts);
      } catch (err) {
        toast.error(getWhatsAppErrorMessage(err, "Failed to load media"));
        if (!append) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [chatId],
  );

  useEffect(() => {
    setActiveType("image");
    setItems([]);
    setOffset(0);
    if (chatId) {
      fetchMedia({ type: "image", nextOffset: 0 });
    }
  }, [chatId, fetchMedia]);

  const handleTab = (type) => {
    setActiveType(type);
    setOffset(0);
    fetchMedia({ type, nextOffset: 0 });
  };

  const activeTab = TABS.find((t) => t.key === activeType) || TABS[0];
  const tabCount = Number(counts?.[activeTab.countKey]) || 0;
  const totalCount = Number(counts?.total) || 0;
  const hasMore = items.length < total;
  const isGrid = activeType === "image" || activeType === "video";

  return (
    <section className="wa-crm-section wa-crm-media">
      <div className="wa-crm-section-head">
        <div className="wa-crm-section-title">
          <Icon icon="solar:folder-with-files-linear" width={20} />
          <span>Media Assets</span>
        </div>
        <span className="wa-crm-pill-count">{totalCount}</span>
      </div>

      <div className="wa-crm-media-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeType === tab.key}
            className={`wa-crm-media-tab ${activeType === tab.key ? "active" : ""}`}
            onClick={() => handleTab(tab.key)}
            title={tab.label}
            aria-label={tab.label}
          >
            <Icon icon={tab.icon} width={22} />
          </button>
        ))}
      </div>

      <div className="wa-crm-media-subtitle">
        <Icon icon={activeTab.icon} width={16} />
        <span>
          {activeTab.label} ({tabCount})
        </span>
      </div>

      {loading ? (
        <div className="wa-crm-media-empty">
          <span className="wa-sidebar-empty">Loading...</span>
        </div>
      ) : items.length === 0 ? (
        <div className="wa-crm-media-empty">
          <Icon icon={activeTab.emptyIcon} width={72} className="wa-crm-media-empty-icon" />
          <p>{activeTab.emptyText}</p>
        </div>
      ) : (
        <>
          <div className={isGrid ? "wa-media-grid" : "wa-media-list"}>
            {items.map((item) => {
              const type = String(item.type || "").toLowerCase();
              const title =
                item.caption ||
                item.filename ||
                (item.mediaId ? `Media ${item.mediaId}` : type);

              if (type === "image" || type === "video") {
                return (
                  <div
                    key={item.id ?? `${item.mediaId}-${item.timestamp}`}
                    className="wa-media-grid-item"
                  >
                    <MediaThumb item={item} onImagePreview={onImagePreview} />
                    <div className="wa-media-grid-meta">
                      <span>{formatRelativeTime(item.timestamp)}</span>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={item.id ?? `${item.mediaId}-${item.timestamp}`}
                  className="wa-media-list-item"
                >
                  <MediaThumb item={item} onImagePreview={onImagePreview} />
                  <div className="wa-media-list-meta">
                    <div className="wa-media-list-title">{title}</div>
                    <div className="wa-media-list-sub">
                      {type}
                      {" · "}
                      {formatRelativeTime(item.timestamp)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore ? (
            <button
              type="button"
              className="whatsapp-load-more"
              disabled={loadingMore}
              onClick={() =>
                fetchMedia({
                  type: activeType,
                  append: true,
                  nextOffset: offset + MEDIA_PAGE_SIZE,
                })
              }
            >
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
